import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, Technique, TechniqueProgress } from "../lib/api";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import {
  Lock,
  CheckCircle2,
  Circle,
  TrendingUp,
  Search,
  ArrowRight,
} from "lucide-react";
import { featureFlags } from "../config/featureFlags";

export default function LearningHub() {
  const navigate = useNavigate();
  const [techniques, setTechniques] = useState<Technique[]>([]);
  const [progress, setProgress] = useState<TechniqueProgress[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [selectedLevel, setSelectedLevel] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState<string>("");

  useEffect(() => {
    const loadData = async () => {
      try {
        const [techniquesData, progressData] = await Promise.all([
          api.getTechniques({ view: "list" }), // Fetch minimal data for list view
          api.getUserProgress().catch(() => []), // Gracefully handle if no progress yet
        ]);
        setTechniques(techniquesData);
        setProgress(progressData);
      } catch (error) {
        console.error("Failed to load data:", error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  const normalizedQuery = searchQuery.trim().toLowerCase();

  const filteredTechniques = techniques.filter((t) => {
    const categoryMatch =
      selectedCategory === "all" || t.category === selectedCategory;
    const levelMatch =
      selectedLevel === "all" ||
      (t.level !== undefined && t.level === parseInt(selectedLevel));
    const searchMatch =
      normalizedQuery.length === 0 ||
      t.name.toLowerCase().includes(normalizedQuery) ||
      (t.description?.toLowerCase() ?? "").includes(normalizedQuery);
    return categoryMatch && levelMatch && searchMatch;
  });

  const getProgressForTechnique = (
    techniqueId: string,
  ): TechniqueProgress | undefined => {
    return progress.find((p) => p.technique_id === techniqueId);
  };

  const isTechniqueMastered = (techniqueId: string): boolean => {
    const prog = getProgressForTechnique(techniqueId);
    return prog ? ["advanced", "expert"].includes(prog.mastery_level) : false;
  };

  const getMasteryColor = (masteryLevel?: string) => {
    switch (masteryLevel) {
      case "expert":
        return "bg-purple-100 text-purple-800";
      case "advanced":
        return "bg-green-100 text-green-800";
      case "intermediate":
        return "bg-blue-100 text-blue-800";
      case "beginner":
        return "bg-yellow-100 text-yellow-800";
      case "novice":
        return "bg-gray-100 text-gray-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getStatusIcon = (techniqueId: string) => {
    const prog = getProgressForTechnique(techniqueId);

    if (!prog) {
      return <Circle className="w-5 h-5 text-gray-400" />;
    }

    if (["advanced", "expert"].includes(prog.mastery_level)) {
      return <CheckCircle2 className="w-5 h-5 text-green-600" />;
    }

    return <TrendingUp className="w-5 h-5 text-blue-600" />;
  };

  const getStatusLabel = (techniqueId: string): string => {
    const prog = getProgressForTechnique(techniqueId);

    if (!prog) return "Not Started";
    if (["advanced", "expert"].includes(prog.mastery_level)) return "Mastered";
    return "In Progress";
  };

  const getButtonText = (techniqueId: string): string => {
    const prog = getProgressForTechnique(techniqueId);

    if (!prog) return "Start Learning";
    if (["advanced", "expert"].includes(prog.mastery_level))
      return "Review Technique";
    return "Continue Learning";
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case "easy":
        return "bg-green-100 text-green-800";
      case "medium":
        return "bg-yellow-100 text-yellow-800";
      case "hard":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getCategoryName = (category: string) => {
    switch (category) {
      case "vedic_math":
        return "Vedic Mathematics";
      case "russian_math":
        return "Russian Math Principles";
      case "universal_tricks":
        return "Universal Tricks";
      case "problem_solving":
        return "Problem Solving";
      default:
        return category;
    }
  };

  if (loading) {
    return <div className="container mx-auto px-4 py-8">Loading...</div>;
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <Button
          variant="ghost"
          className="text-muted-foreground hover:text-foreground mb-4 pl-0"
          onClick={() => navigate("/arena")}
        >
          <ArrowRight className="w-4 h-4 mr-2 rotate-180" />
          Back to Arena
        </Button>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
          Learning Hub
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          Master powerful mathematical techniques
        </p>
        <p className="text-sm text-gray-500 dark:text-gray-500 mt-1">
          {filteredTechniques.length}{" "}
          {filteredTechniques.length === 1 ? "technique" : "techniques"}{" "}
          available
        </p>
      </div>

      <Tabs value={selectedCategory} onValueChange={setSelectedCategory}>
        <div className="flex flex-col gap-4 mb-6">
          <TabsList className="w-full overflow-x-auto">
            <TabsTrigger value="all">All Techniques</TabsTrigger>
            <TabsTrigger value="vedic_math">Vedic Math</TabsTrigger>
            <TabsTrigger value="universal_tricks">Universal Tricks</TabsTrigger>
            <TabsTrigger value="russian_math">Russian Math</TabsTrigger>
            {featureFlags.OLYMPIAD_TRACK && (
              <TabsTrigger value="problem_solving">Math Olympiad</TabsTrigger>
            )}
          </TabsList>

          <div className="flex flex-col sm:flex-row gap-4 sm:items-center">
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Level:
              </label>
              <Select value={selectedLevel} onValueChange={setSelectedLevel}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="All Levels" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Levels</SelectItem>
                  <SelectItem value="1">Level 1</SelectItem>
                  <SelectItem value="2">Level 2</SelectItem>
                  <SelectItem value="3">Level 3</SelectItem>
                  <SelectItem value="4">Level 4</SelectItem>
                  <SelectItem value="5">Level 5</SelectItem>
                  <SelectItem value="6">Level 6</SelectItem>
                  {featureFlags.OLYMPIAD_TRACK && (
                    <>
                      <SelectItem value="7">Level 7 (Olympiad)</SelectItem>
                      <SelectItem value="8">Level 8 (Olympiad)</SelectItem>
                      <SelectItem value="9">Level 9 (Olympiad)</SelectItem>
                      <SelectItem value="10">Level 10 (Olympiad)</SelectItem>
                      <SelectItem value="11">Level 11 (Olympiad)</SelectItem>
                    </>
                  )}
                </SelectContent>
              </Select>
            </div>

            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search techniques..."
                className="pl-10"
              />
            </div>
          </div>
        </div>

        <TabsContent value={selectedCategory}>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredTechniques.map((technique) => {
              const techniqueProgress = getProgressForTechnique(technique.id);
              const mastered = isTechniqueMastered(technique.id);

              return (
                <Card
                  key={technique.id}
                  className="hover:shadow-lg transition-shadow relative"
                >
                  <CardHeader>
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-start gap-3 flex-1 pr-2">
                        <div className="mt-1">
                          {getStatusIcon(technique.id)}
                        </div>
                        <div className="flex-1">
                          <CardTitle className="text-lg mb-1">
                            {technique.name}
                          </CardTitle>
                          <span
                            className={`text-xs font-medium ${
                              mastered
                                ? "text-green-600"
                                : techniqueProgress
                                  ? "text-blue-600"
                                  : "text-gray-500"
                            }`}
                          >
                            {getStatusLabel(technique.id)}
                          </span>
                        </div>
                      </div>
                      {technique.is_premium &&
                        featureFlags.SHOW_PRO_BADGES &&
                        featureFlags.PREMIUM_TECHNIQUES && (
                          <Badge
                            variant="secondary"
                            className="flex items-center gap-1 flex-shrink-0"
                          >
                            <Lock className="w-3 h-3" />
                            PRO
                          </Badge>
                        )}
                    </div>
                    <div className="flex flex-wrap gap-2 mb-2">
                      {technique.level && (
                        <Badge
                          variant="outline"
                          className="bg-blue-100 text-blue-800 text-xs"
                        >
                          Level {technique.level}
                        </Badge>
                      )}
                      {techniqueProgress && (
                        <Badge
                          variant="outline"
                          className={`text-xs capitalize ${getMasteryColor(techniqueProgress.mastery_level)}`}
                        >
                          {techniqueProgress.mastery_level}
                        </Badge>
                      )}
                      <Badge
                        variant="outline"
                        className={`text-xs ${getDifficultyColor(technique.difficulty)}`}
                      >
                        {technique.difficulty}
                      </Badge>
                      <Badge variant="outline" className="text-xs">
                        {getCategoryName(technique.category)}
                      </Badge>
                    </div>
                    <CardDescription className="line-clamp-2">
                      {technique.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Link to={`/technique/${technique.id}`}>
                      <Button
                        className="w-full"
                        variant={mastered ? "outline" : "default"}
                      >
                        {getButtonText(technique.id)}
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {filteredTechniques.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              <p>No techniques found in this category</p>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
